import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import toast from 'react-hot-toast';

import {
  PatientAccessLevel,
  PublicServiceSignUpError,
  usePatientPortalBookServiceMutation,
  usePublicProviderServiceQuery,
  usePublicServiceSignUpMutation,
} from '../../../generated/graphql';
import ToastAlert from '../../components/ToastAlert';

import { HOMEPAGE_URL } from '../../lib/constants';
import { getSafeHttpRedirectUrl } from '../../lib/url';
import { portalBookErrorMessage } from '../../lib/patientPortalBookService';
import PageContainer from '../../components/Containers/PageContainer';
import useDisableOverscroll from '../../hooks/useDisableOverscroll';
import Loading from '../Loading';
import { Transition } from '@headlessui/react';
import { ArrowLeftIcon } from '@heroicons/react/outline';
import Button from '../../components/Button';
import IconButton from '../../components/IconButton';
import { defaultTransitionProps } from '../../lib/animation';

import usePaintScreen from '../../hooks/usePaintScreen';
import Avatar from '../../components/Avatar';
import classNames from 'classnames';
import {
  CALENDLY_COLOR_MAP,
  getIconComponent,
} from '../../components/ServiceCard/helpers';

import ErrorMessage from '../../components/ErrorMessage';
import InputGroup from '../../components/InputGroup';
import { NAME_REGEX, EMAIL_REGEX } from '../../lib/regex';
import { DEFAULT_CTA_BUTTON_TEXT } from '../MyPage';
import { useForm } from 'react-hook-form';
import { usePatientAuth } from '../../../contexts/PatientAuthContext';
import ServiceIntakeCompleteModal from '../../components/Modals/ServiceIntakeCompleteModal';
import Spinner from '../../svgs/Spinner';
import { useProviderColors } from '../../hooks/useProviderColors';
import Editor from '../../components/Editor';

interface PublicServiceSignUpFormData {
  fullName: string;
  email: string;
}

const PublicServicePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  useDisableOverscroll();
  const paintScreen = usePaintScreen();
  const {
    authedPatient,
    patientTokenPayload,
    refreshAuthedPatient,
    updateTokenPayload,
  } = usePatientAuth();
  const [checkYourEmail, setCheckYourEmail] = useState(false);

  useEffect(() => {
    if (!patientTokenPayload || authedPatient) {
      return;
    }

    const loadAuthedPatient = async () => {
      try {
        await refreshAuthedPatient();
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        if (message !== 'Invalid bearer token') {
          navigate('/login/client');
        }
      }
    };

    loadAuthedPatient();
  }, [patientTokenPayload, authedPatient, refreshAuthedPatient, navigate]);

  const slug = params.slug;
  const serviceSlug = params.serviceSlug;

  const fromIntakeSuccess = (location.state as { fromIntakeSuccess?: boolean })
    ?.fromIntakeSuccess;

  const { data: publicServiceData, loading: publicServiceLoading } =
    usePublicProviderServiceQuery({
      variables: {
        slug,
        serviceSlug,
      },
      skip: !slug || !serviceSlug,
      onError: () => {
        window.location.href = HOMEPAGE_URL;
      },
    });

  const publicService = publicServiceData?.publicProviderService;

  const { useProviderPrimaryColor } = useProviderColors({
    providerPrimaryColor: publicService?.providerPrimaryColor,
    providerSecondaryColor: publicService?.providerSecondaryColor,
  });

  const showPage = paintScreen && !publicServiceLoading;

  const isNotAllAccess =
    publicService?.patientAccessLevel !== PatientAccessLevel.All;
  const isNewPatientsAccess =
    publicService?.patientAccessLevel === PatientAccessLevel.New;
  const isCurrentPatientsAccess =
    publicService?.patientAccessLevel === PatientAccessLevel.Current;

  const IconComponent = getIconComponent(publicService?.serviceIcon);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors: formErrors, isSubmitting: isFormSubmitting },
  } = useForm<PublicServiceSignUpFormData>();

  const [publicServiceSignUp, { loading: isPublicServiceSignUpLoading }] =
    usePublicServiceSignUpMutation();

  const [patientPortalBookService, { loading: isPortalBookingInFlight }] =
    usePatientPortalBookServiceMutation();

  // True when the visitor is already logged in as a patient of THIS provider
  // and the service is open to existing clients. In that case we skip the
  // public email-collection form and let them book in one click — no
  // patient-confirmation or provider-email noise.
  const isAuthedClientForThisProvider =
    !!authedPatient &&
    !!publicService &&
    authedPatient.providerStorefrontSlug === slug &&
    authedPatient.providerAllowsPortalServiceBooking === true &&
    publicService.patientAccessLevel !== PatientAccessLevel.New;

  const onPortalBook = async () => {
    if (!publicService) return;
    let payload;
    try {
      const result = await patientPortalBookService({
        variables: { input: { providerServiceId: publicService.id } },
      });
      payload = result.data?.patientPortalBookService;
    } catch {
      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          message={portalBookErrorMessage()}
          level="error"
        />
      ));
      return;
    }

    if (!payload || payload.error || !payload.serviceIntakePackageId) {
      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          message={portalBookErrorMessage(payload?.error)}
          level="error"
        />
      ));
      return;
    }

    navigate(
      `/client/intake/${payload.serviceIntakePackageId}?fromPortal=true`,
    );
  };

  const onSubmit = async (formData: PublicServiceSignUpFormData) => {
    if (!slug || !serviceSlug) {
      return;
    }

    const email = formData.email.trim();
    const fullName = formData.fullName.trim();

    try {
      const { data } = await publicServiceSignUp({
        variables: {
          input: {
            email,
            name: fullName,
            storefrontSlug: slug,
            serviceSlug,
          },
        },
      });

      const publicServiceSignUpData = data?.publicServiceSignUp;

      if (publicServiceSignUpData?.error) {
        const publicServiceSignUpError = publicServiceSignUpData.error;
        switch (publicServiceSignUpError) {
          case PublicServiceSignUpError.InvalidServiceSlug:
            setError('root.serverError', {
              message: `We couldn't find the service you're trying to connect with. Please try again later.`,
            });
            break;
          case PublicServiceSignUpError.EmailAlreadyExistsOtherProvider:
            setError('root.serverError', {
              message: `It looks like you're already working with another provider on Homecoming. Please provide another email to work with ${publicService?.providerName}`,
            });
            break;
          case PublicServiceSignUpError.EmailAlreadyExistsThisProvider:
            setCheckYourEmail(true);
            break;
          case PublicServiceSignUpError.CurrentClientsOnly:
            setError('root.serverError', {
              message: `It looks like you're a new client. This service is only available for current clients.`,
            });
            break;
          case PublicServiceSignUpError.NewClientsOnly:
            setError('root.serverError', {
              message: `It looks like you're an existing client. This service is only available for new clients.`,
            });
            break;
          case PublicServiceSignUpError.InvalidName:
            setError('fullName', {
              message: 'Please enter a valid name',
            });
            break;
          case PublicServiceSignUpError.InvalidEmail:
            setError('email', {
              message: 'Please enter a valid email',
            });
            break;
          default:
            break;
        }
        return;
      }

      if (publicServiceSignUpData?.authToken) {
        updateTokenPayload({
          authSessionToken: publicServiceSignUpData.authToken,
        });
        navigate(
          `/client/intake/${publicServiceSignUpData.serviceIntakePackageId}`,
          {
            state: {
              fullName,
            },
          },
        );
      }
    } catch (error) {
      setError('root.serverError', {
        message:
          "Something went wrong. Please contact support@homecoming.health and we'll help you get connected.",
      });
    }
  };

  const [
    isServiceIntakeCompleteModalOpen,
    setIsServiceIntakeCompleteModalOpen,
  ] = useState(false);

  useEffect(() => {
    if (publicService && fromIntakeSuccess) {
      const redirectUrl = getSafeHttpRedirectUrl(
        publicService.serviceSubmissionRedirectUrl,
      );
      if (redirectUrl) {
        // Only redirect if not already redirected in this session
        if (!sessionStorage.getItem('serviceRedirected')) {
          sessionStorage.setItem('serviceRedirected', 'true');
          window.location.href = redirectUrl;
        }
        return;
      }

      setIsServiceIntakeCompleteModalOpen(true);
      const timeout = setTimeout(() => {
        if (publicService.patientAccessLevel === PatientAccessLevel.Current) {
          navigate('/client', { replace: true });
        }
      }, 3500);
      return () => clearTimeout(timeout);
    }
  }, [publicService, fromIntakeSuccess, navigate]);

  const isStorefrontEnabled = publicService?.storefrontEnabled;

  const HIDE_BACK_TO_HOMEPAGE_SLUGS = ['fireside-project'];

  const hideBackToHomepage = slug && HIDE_BACK_TO_HOMEPAGE_SLUGS.includes(slug);

  return (
    <>
      {/* "Private link only" services stay bookable by URL but shouldn't surface in search results */}
      {publicService && !publicService.isShownOnStorefront && (
        <Helmet>
          <meta name="robots" content="noindex" />
        </Helmet>
      )}
      {publicService ? (
        <div
          className={classNames('relative scroll-smooth', 'min-h-[100vh-64px]')}
        >
          {isStorefrontEnabled && !hideBackToHomepage && (
            <Transition show={showPage} {...defaultTransitionProps}>
              <div className="t-16 fixed z-10 flex w-full flex-row items-center justify-start border-b border-neutral-50 bg-white py-3 px-6 md:px-20">
                <div className="w-[289px]">
                  <IconButton
                    IconComponent={ArrowLeftIcon}
                    iconClassName="text-secondary-100 w-5"
                    aria-label="Back to homepage"
                    className="rounded-xl px-3 py-2"
                    onClick={() => navigate(`/p/${slug}`)}
                  >
                    <div className="small-caption ml-2 font-bold text-secondary-100">
                      Back home
                    </div>
                  </IconButton>
                </div>
              </div>
            </Transition>
          )}

          <PageContainer
            containerClassName={classNames(
              'h-[calc(100vh-var(--top-nav-height))] md:overflow-y-auto',
              'pb-8 md:pb-0 px-8 pt-16 md:px-24 min-w-full',
            )}
            noPadding
          >
            <div className="flex h-full w-full flex-col gap-6 py-8 md:flex-row md:gap-12">
              {/* Service info — half width on md+, full width stacked on mobile */}
              <div className="flex w-full min-w-0 flex-col md:flex-1">
                <div className="mb-10 flex items-center gap-4">
                  <Avatar
                    imageUrl={publicService.profileImageUrl}
                    name={publicService.providerName}
                    size="medium-large"
                  />
                  <div className="text-neutral-125">
                    <div className="font-serif text-subtitle-small font-light">
                      {publicService.providerName}
                    </div>
                    <div className="text-small-caption">
                      {publicService.providerRoleTitle}
                    </div>
                  </div>
                </div>

                <div
                  className={classNames(
                    'mb-12 mt-4 w-full min-w-0 max-w-full overflow-x-clip md:pr-8',
                  )}
                >
                  <div className="mb-6 flex w-full min-w-0 max-w-full items-center justify-between">
                    <div className="flex flex-row items-end justify-start">
                      <IconComponent
                        className={classNames(
                          'h-12 w-12',
                          publicService.serviceIconColorUseCalendlyDefault &&
                            publicService.calendlyEventColor
                            ? CALENDLY_COLOR_MAP[
                                publicService.calendlyEventColor
                              ]
                            : useProviderPrimaryColor
                            ? 'text-primary-125'
                            : 'text-blue-100',
                        )}
                      />
                      {isNotAllAccess && (
                        <div
                          className={classNames(
                            'ml-3 rounded-full px-3 py-1 text-small-caption',
                            isNewPatientsAccess && 'bg-green-25 text-green-150',
                            isCurrentPatientsAccess &&
                              'bg-blue-25 text-blue-150',
                          )}
                        >
                          {isNewPatientsAccess && 'New clients only'}
                          {isCurrentPatientsAccess && 'Current clients only'}
                        </div>
                      )}
                    </div>
                    {publicService.isPriceLabelShown !== false && (
                      <div className="text-category font-medium text-green-150">
                        {publicService.isPaid && publicService.price
                          ? `$${publicService.price}`
                          : 'Free'}
                      </div>
                    )}
                  </div>
                  <div className="mb-2 font-serif text-subtitle-small font-light md:text-subtitle">
                    {publicService.name}
                  </div>
                  {Boolean(publicService.durationMinutes) && (
                    <p className="mb-4 text-small-caption text-neutral-110">
                      {publicService.durationMinutes} minutes
                    </p>
                  )}
                  <div className="flex w-full min-w-0 flex-col">
                    <Editor
                      readOnly
                      initialContentMarkdown={publicService.description ?? ''}
                      elKey={`public-service-${publicService.id}-description`}
                      className={classNames(
                        'prose prose-sm min-w-0 max-w-none overflow-hidden break-words text-caption text-neutral-125',
                        '[&_a]:text-secondary-50 [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1',
                      )}
                      contentClassName="!text-caption min-h-0 break-words overflow-x-hidden [overflow-wrap:anywhere]"
                      containerClassName="w-full min-w-0 max-w-full overflow-hidden border-0"
                      allowLinks
                      onError={(error: Error) => {
                        console.error(error);
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Booking form — half width on md+, full width stacked on mobile */}
              <div className="flex w-full min-w-0 flex-col items-center justify-center md:h-full md:flex-1">
                <div className="flex w-full max-w-full flex-col rounded-lg bg-white p-8 shadow-lg md:min-h-[500px]">
                  <div className="flex h-full w-full flex-col">
                    {/* Top section with fixed spacing */}
                    <div className="flex-1" />

                    {/* Center content that won't shift */}
                    <div className="flex flex-col items-center">
                      {isAuthedClientForThisProvider ? (
                        <>
                          <div className="mb-8 flex flex-col items-center justify-start text-center text-neutral-125 md:mb-12">
                            <div className="text-subtitle-small md:text-subtitle">
                              Welcome back
                              {authedPatient?.firstName
                                ? `, ${authedPatient.firstName}`
                                : ''}
                            </div>
                            <div className="text-caption">
                              You're signed in to your Client Portal — book this
                              service with one click.
                            </div>
                          </div>
                          <Button
                            title="Book this service"
                            className="mx-auto mb-4 w-full disabled:cursor-default md:mt-8"
                            onClick={onPortalBook}
                            disabled={isPortalBookingInFlight}
                            {...(isPortalBookingInFlight && {
                              IconComponent: Spinner,
                              iconClassName: '!h-5 !w-5',
                            })}
                          />
                          <div className="h-12" />
                        </>
                      ) : (
                        <>
                          <div className="mb-8 flex flex-col items-center justify-start text-neutral-125 md:mb-12">
                            <div className="text-subtitle-small md:text-subtitle">
                              Get started
                            </div>
                            <div className="text-caption">
                              Enter your details
                            </div>
                          </div>
                          <form className="w-full">
                            <InputGroup
                              placeholder="Full name"
                              autoComplete="name"
                              type="text"
                              inputSize="small"
                              useNaturalLettering={true}
                              containerClassName="mb-4"
                              required
                              errorMessage={formErrors.fullName?.message}
                              {...register('fullName', {
                                required: 'Name is required',
                                setValueAs: (v) =>
                                  typeof v === 'string' ? v.trim() : v,
                                pattern: {
                                  value: NAME_REGEX,
                                  message:
                                    'Please enter your first and last name',
                                },
                              })}
                            />
                            <InputGroup
                              placeholder="Email"
                              autoComplete="email"
                              type="email"
                              inputSize="small"
                              useNaturalLettering={true}
                              containerClassName="mb-6 md:mb-2"
                              required
                              errorMessage={formErrors.email?.message}
                              {...register('email', {
                                required: 'Email is required',
                                setValueAs: (v) =>
                                  typeof v === 'string' ? v.trim() : v,
                                pattern: {
                                  value: EMAIL_REGEX,
                                  message: 'Please enter a valid email',
                                },
                              })}
                            />
                            <Button
                              title={DEFAULT_CTA_BUTTON_TEXT}
                              className="mx-auto mb-4 w-full disabled:cursor-default md:mt-8"
                              type="submit"
                              disabled={isFormSubmitting || checkYourEmail}
                              onClick={handleSubmit(onSubmit)}
                              {...(isPublicServiceSignUpLoading && {
                                IconComponent: Spinner,
                                iconClassName: '!h-5 !w-5',
                              })}
                            />
                            {/* Error message in fixed height container */}
                            <div className="h-12">
                              {' '}
                              {/* Fixed height for error message */}
                              <ErrorMessage
                                className="w-full"
                                textClassName={classNames(
                                  'text-center w-full text-caption',
                                  !formErrors.root?.serverError &&
                                    checkYourEmail &&
                                    'text-green-125',
                                )}
                              >
                                {formErrors.root?.serverError?.message ||
                                  (checkYourEmail &&
                                    "We've sent you an email with a link to your client portal.")}
                              </ErrorMessage>
                            </div>
                          </form>
                        </>
                      )}
                    </div>

                    {/* Bottom section with fixed spacing */}
                    <div className="flex-1" />
                  </div>
                </div>
              </div>
            </div>
          </PageContainer>
        </div>
      ) : (
        <Loading />
      )}
      <ServiceIntakeCompleteModal
        isModalOpen={isServiceIntakeCompleteModalOpen}
        setClosed={() => setIsServiceIntakeCompleteModalOpen(false)}
        avatarImageUrl={publicService?.profileImageUrl}
        avatarName={publicService?.providerName}
        title={publicService?.serviceSubmissionTitle}
        body={publicService?.serviceSubmissionBody}
      />
    </>
  );
};

export default PublicServicePage;
